import { type BulkQueueStatus, ICON_PROPS, formatDuration, formatRelative, parseHostMessage, postToHost, shortDate } from './shared.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
import { type BridgeBus, type BridgeEvent, type IndexProgress, type IndexState } from './shared.js';
import { FolderLoadingState } from './shared-components.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — index tab cards + panel,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function FolderCoverageCard({ outlookFolders, mailCountByFolder }: {
  outlookFolders: BridgeBus['outlookFolders'];
  mailCountByFolder: Record<string, number>;
}): React.JSX.Element | null {
  const [counts, setCounts] = useState<Awaited<ReturnType<typeof api.addinFolderCounts>> | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    try { setCounts(await api.addinFolderCounts()); } finally { setBusy(false); }
  }
  useEffect(() => { refresh(); }, []);

  if (!counts && !busy) return null;
  if (!counts) return <Card padding="md" withBorder><Center p="md"><Loader size="sm" /></Center></Card>;

  // Join brain counts with Outlook's reported item_count per folder.
  const outlookByPath = new Map<string, number>();
  // 3.5.46: prefer mail-only count from a recent index_complete event;
  // fall back to total Items.Count when no recent run has happened yet.
  // The mail-only count excludes Calendar / NDR / hidden / Post items
  // — which the addin's IPM.Note filter correctly skips, so they
  // shouldn't count against the coverage ratio.
  for (const f of outlookFolders) {
    const mailCount = mailCountByFolder[f.path];
    if (typeof mailCount === 'number' && mailCount > 0) {
      outlookByPath.set(f.path, mailCount);
    } else if (f.item_count != null) {
      outlookByPath.set(f.path, f.item_count);
    }
  }
  // Round 28-AC: classify each row into one of three states:
  //   undercovered → indexed < 90% of Outlook → fix Cached Exchange Mode
  //   tombstones   → indexed >> Outlook (>5%) → brain has rows for emails
  //                  the user deleted from Outlook; not a coverage problem,
  //                  just rotting rows. Render with a different icon.
  //   ok           → 90% ≤ indexed/outlook ≤ 105%
  // Folders with 0 Outlook items + 0 indexed are dropped (no signal).
  type CovRow = {
    folder: string;
    indexed: number;
    outlook: number;
    latest: string | null;
    pct: number;       // capped 0-100 for display
    state: 'ok' | 'undercovered' | 'tombstones';
    tombstones: number; // indexed - outlook when positive
  };
  const rows: CovRow[] = counts.folders
    .map((c): CovRow | null => {
      const outlook = outlookByPath.get(c.folder);
      if (outlook == null) return null;
      if (outlook === 0 && c.msgs === 0) return null;
      const ratio = outlook > 0 ? c.msgs / outlook : (c.msgs > 0 ? 99 : 1);
      let state: CovRow['state'] = 'ok';
      if (ratio < 0.9) state = 'undercovered';
      else if (ratio > 1.05 && c.msgs - outlook >= 5) state = 'tombstones';
      const pct = outlook > 0 ? Math.min(100, Math.round((c.msgs / outlook) * 100)) : 100;
      return {
        folder: c.folder,
        indexed: c.msgs,
        outlook,
        latest: c.latest,
        pct,
        state,
        tombstones: Math.max(0, c.msgs - outlook),
      };
    })
    .filter((r): r is CovRow => r !== null);
  // Sort worst-first so under-covered rises to the top.
  rows.sort((a, b) => {
    const order = { undercovered: 0, tombstones: 1, ok: 2 };
    if (order[a.state] !== order[b.state]) return order[a.state] - order[b.state];
    if (a.state === 'undercovered') return a.pct - b.pct;
    if (a.state === 'tombstones') return b.tombstones - a.tombstones;
    return b.indexed - a.indexed;
  });
  const undercovered = rows.filter((r) => r.state === 'undercovered');
  const tombstoneRows = rows.filter((r) => r.state === 'tombstones');
  const tombstoneTotal = tombstoneRows.reduce((acc, r) => acc + r.tombstones, 0);

  const borderColor = undercovered.length > 0 ? 'orange' : tombstoneRows.length > 0 ? 'gray' : 'green';

  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-' + borderColor + '-6)' }}>
      <Group justify="space-between" mb="xs">
        <Stack gap={0}>
          <Title order={5}>Folder coverage</Title>
          <Text size="xs" c="dimmed">
            How many of each folder's emails are indexed. A gap means Outlook itself doesn't have the messages — IMAP "keep offline" limit, POP server-side history, or Exchange Cached Mode.
          </Text>
        </Stack>
        <Button size="xs" variant="default" onClick={refresh} loading={busy}>Refresh</Button>
      </Group>
      {undercovered.length > 0 && (
        <Alert color="orange" variant="light" mb="xs">
          <Stack gap={4}>
            <Text size="xs" fw={500}>
              {undercovered.length} folder{undercovered.length === 1 ? '' : 's'} under-covered — Outlook itself doesn't have all the messages, so the addin can't index them. Fix at the Outlook end:
            </Text>
            <Text size="xs">
              <b>IMAP:</b> File → Account Settings → that account → Change → drag "Mail to keep offline" all the way to <b>All</b>. Click Next, restart Outlook, wait for sync to finish, then re-index.
            </Text>
            <Text size="xs">
              <b>POP:</b> POP only downloads new mail. Older mail kept on the server isn't visible to Outlook (or to us). If the original mailbox is still on the server, switch to IMAP to get full history.
            </Text>
            <Text size="xs">
              <b>Exchange:</b> File → Account Settings → that account → Change → uncheck "Use Cached Exchange Mode" OR slide "Mail to keep offline" to All.
            </Text>
            <Text size="xs" c="dimmed">
              After Outlook finishes downloading, click "Force full re-index" above to walk the newly-visible mail.
            </Text>
          </Stack>
        </Alert>
      )}
      {tombstoneTotal > 0 && (
        <Alert color="gray" variant="light" mb="xs">
          <Text size="xs">
            <b>{tombstoneTotal.toLocaleString()}</b> tombstone{tombstoneTotal === 1 ? '' : 's'} across {tombstoneRows.length} folder{tombstoneRows.length === 1 ? '' : 's'} — rows for emails you deleted in Outlook. Search still works; they just inflate the index size.
          </Text>
        </Alert>
      )}
      <Stack gap={2}>
        {rows.slice(0, 30).map((r) => {
          const tone = r.state === 'undercovered' ? 'orange' : r.state === 'tombstones' ? 'gray' : 'dimmed';
          const fw = r.state === 'undercovered' ? 600 : 400;
          const label =
            r.state === 'tombstones'
              ? `${r.indexed.toLocaleString()} / ${r.outlook.toLocaleString()} (+${r.tombstones.toLocaleString()} stale)`
              : `${r.indexed.toLocaleString()} / ${r.outlook.toLocaleString()} (${r.pct}%)`;
          return (
            <Group key={r.folder} justify="space-between" wrap="nowrap" gap="xs">
              <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{r.folder}</Text>
              <Text size="xs" c={tone} fw={fw}>{label}</Text>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}

// =========================================================================
// PHASE 2 — Index Health Card (Round 49)
// Birds-eye view per store. Aggregates per-folder coverage into store-level
// numbers + flags stale folders. Quick-glance trust check.
// =========================================================================
export function IndexHealthCard(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinIndexHealth>> | null>(null);
  const [busy, setBusy] = useState(false);
  async function load() {
    setBusy(true);
    try { setData(await api.addinIndexHealth()); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);
  if (!data && !busy) return null;
  if (!data) return <Card padding="md" withBorder><Center p="md"><Loader size="sm" /></Center></Card>;
  const stale = data.stale_folders;
  const ok = stale === 0;
  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-' + (ok ? 'green' : 'orange') + '-6)' }}>
      <Group justify="space-between" mb="xs">
        <Stack gap={0}>
          <Title order={5}>Index health</Title>
          <Text size="xs" c="dimmed">
            Snapshot of what's in the brain right now, per Outlook store.
          </Text>
        </Stack>
        <Button size="xs" variant="default" onClick={load} loading={busy}>Refresh</Button>
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 5 }} mb="md">
        <Stack gap={0} align="center">
          <Text fw={700} size="lg">{data.total_indexed.toLocaleString()}</Text>
          <Text size="xs" c="dimmed">Total indexed</Text>
        </Stack>
        <Stack gap={0} align="center">
          <Text fw={700} size="lg" c={data.indexed_last_hour > 0 ? 'green' : 'dimmed'}>
            {data.indexed_last_hour.toLocaleString()}
          </Text>
          <Text size="xs" c="dimmed">Indexed last 1h</Text>
        </Stack>
        <Stack gap={0} align="center">
          <Text fw={700} size="lg">{data.received_last_24h.toLocaleString()}</Text>
          <Text size="xs" c="dimmed">Received last 24h</Text>
        </Stack>
        <Stack gap={0} align="center">
          <Text fw={700} size="lg" c={stale > 0 ? 'orange' : 'green'}>{stale}</Text>
          <Text size="xs" c="dimmed">Stale &gt;7d (live)</Text>
        </Stack>
        <Stack gap={0} align="center">
          <Text fw={700} size="lg">
            {(data.total_stores - (data.archive_stores ?? 0))}
            {data.archive_stores ? <Text span size="xs" c="dimmed"> + {data.archive_stores} arch</Text> : null}
          </Text>
          <Text size="xs" c="dimmed">Mailbox stores</Text>
        </Stack>
      </SimpleGrid>
      {data.stores.length > 0 && (
        <>
          <Divider label="Per-store breakdown" labelPosition="left" mb="xs" />
          <Stack gap={4}>
            {data.stores.map((s) => {
              const isArchive = s.is_archive === true;
              const isStaleLive = !isArchive && s.latest && new Date(s.latest).getTime() < Date.now() - 7 * 24 * 60 * 60 * 1000;
              return (
                <Group key={s.store} justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" lineClamp={1}>{s.store}</Text>
                    {isArchive && <Badge size="xs" color="gray" variant="light">ARCHIVE</Badge>}
                  </Group>
                  <Text size="xs" c={isArchive ? 'dimmed' : isStaleLive ? 'orange' : 'dimmed'}>
                    {s.msgs.toLocaleString()} msgs · {s.folders} folders · newest {s.latest ? formatRelative(s.latest) : 'never'}
                    {isStaleLive && ' ⚠'}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        </>
      )}
    </Card>
  );
}

// Round 36 follow-up: "Repair missing bodies" card. Background: a meaningful
// number of users have indexed messages where body_redacted is NULL — the
// fallout from the pre-round-32 pipeline bug that hard-coded the body to
// null on every IMAP/COM-ingest. Search, Ask AI, drafts, briefing all
// silently degrade for those rows because there's no content to read.
//
// The brain's index-queue/processor.ts has an empty-body REPAIR path: when
// a duplicate message_id arrives with a non-empty body, the existing row
// gets UPDATEd in place. The COM addin's StartBodyFillPassAsync already
// walks every Outlook folder, reads .Body for each item, and re-uploads —
// triggering the repair for every empty-body row.
//
// All that was missing: a UI button that triggers the body-fill pass
// directly (without first running the metadata re-walk).
export function RepairBodiesCard(): React.JSX.Element {
  const [emptyCount, setEmptyCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ folder: string; processed: number; repaired: number; elapsed_ms: number } | null>(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.addinIndexEmptyBodies();
      setEmptyCount(r.remaining_total);
    } catch { /* brain unreachable — leave null */ }
  }, []);

  useEffect(() => { loadCount(); }, [loadCount]);

  // Listen to host events from the COM addin so we can show live progress
  // and refresh the count on completion.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const m = parseHostMessage(e.data);
      if (!m) return;
      if (m.type === 'body_fill_started') {
        setBusy(true);
        setProgress({ folder: '', processed: 0, repaired: 0, elapsed_ms: 0 });
      } else if (m.type === 'body_fill_progress') {
        setProgress({ folder: m.folder, processed: m.processed, repaired: m.repaired, elapsed_ms: m.elapsed_ms });
      } else if (m.type === 'body_fill_complete') {
        setBusy(false);
        setProgress(null);
        notifications.show({
          color: 'green',
          message: `Body repair: ${m.processed.toLocaleString()} scanned, ${m.repaired.toLocaleString()} bodies filled in ${formatDuration(m.elapsed_ms / 1000)}.`,
        });
        loadCount();
      } else if ((m as { type?: string }).type === 'body_fill_partial') {
        // Capped at 5000 per pass. Refresh count, prompt the user that
        // more is left.
        setBusy(false);
        setProgress(null);
        notifications.show({
          color: 'blue',
          message: 'Batch done — more bodies remain. Click Repair again to continue.',
        });
        loadCount();
      } else if ((m as { type?: string }).type === 'body_fill_skipped') {
        setBusy(false);
        setProgress(null);
        notifications.show({ color: 'gray', message: 'No empty bodies to repair.' });
        loadCount();
      }
    };
    const wv = window.chrome?.webview;
    if (wv?.addEventListener) {
      wv.addEventListener('message', handler);
      return () => { try { wv.removeEventListener?.('message', handler); } catch { /* ignore */ } };
    }
    window.addEventListener('message', handler as EventListener);
    return () => window.removeEventListener('message', handler as EventListener);
  }, [loadCount]);

  function startRepair(): void {
    setBusy(true);
    postToHost({ type: 'repair_missing_bodies' });
  }

  // Hide the card entirely when there's nothing to do. Reduces clutter for
  // users without any empty-body backlog.
  if (emptyCount === 0) return <></>;

  return (
    <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-orange-6)' }}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Stack gap={2}>
            <Title order={5}>Repair missing bodies</Title>
            <Text size="sm" c="dimmed">
              {emptyCount === null
                ? 'Checking for messages with empty bodies…'
                : emptyCount > 0
                  ? <><b>{emptyCount.toLocaleString()}</b> messages indexed without body content. Search, Ask AI, drafts skip them.</>
                  : 'No empty bodies — search and Ask AI have full content to work with.'}
            </Text>
            <Text size="xs" c="dimmed">
              Walks your Inbox + Sent of every store, fetches the body for each item locally,
              re-uploads to the brain. Up to 5,000 per click — re-click to continue if more remain.
            </Text>
          </Stack>
          <Button
            color="orange"
            onClick={startRepair}
            disabled={busy || !emptyCount}
            loading={busy}
          >
            {busy ? 'Repairing…' : 'Repair missing bodies'}
          </Button>
        </Group>
        {progress && (
          <Paper p="xs" withBorder style={{ background: 'var(--mantine-color-orange-0)' }}>
            <Group justify="space-between" wrap="nowrap">
              <Text size="xs" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                {progress.folder || 'starting…'}
              </Text>
              <Text size="xs" c="dimmed">
                {progress.processed.toLocaleString()} scanned · {progress.repaired.toLocaleString()} filled · {formatDuration(progress.elapsed_ms / 1000)}
              </Text>
            </Group>
          </Paper>
        )}
      </Stack>
    </Card>
  );
}

// "Why isn't this email indexed?" diagnostic.
export function DiagnoseEmailCard(): React.JSX.Element {
  const [subject, setSubject] = useState('');
  const [from, setFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.addinDiagnoseEmail>> | null>(null);
  async function diagnose() {
    if (!subject.trim() && !from.trim()) {
      notifications.show({ color: 'orange', message: 'Enter a subject keyword or sender email.' });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.addinDiagnoseEmail({
        ...(subject.trim() ? { subject: subject.trim() } : {}),
        ...(from.trim() ? { from: from.trim() } : {}),
      });
      setResult(r);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally { setBusy(false); }
  }
  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-cyan-6)' }}>
      <Stack gap="sm">
        <Stack gap={0}>
          <Title order={5}>Find missing email</Title>
          <Text size="xs" c="dimmed">
            Tell me what email you expected to see but couldn't. I'll check the index and tell you why.
          </Text>
        </Stack>
        <Group gap="xs">
          <TextInput
            placeholder="Subject keyword (e.g. Total Credit Limit)"
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <TextInput
            placeholder="Sender (e.g. infobip.com)"
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <Button onClick={diagnose} loading={busy} size="sm">Diagnose</Button>
        </Group>
        {result && (
          <Card padding="sm" radius="md" withBorder
            style={{ borderLeft: '3px solid var(--mantine-color-' + (result.exact_matches.length > 0 ? 'green' : 'orange') + '-6)' }}>
            <Stack gap="xs">
              <Text size="sm" fw={500}>{result.reason}</Text>
              <Text size="xs" c="dimmed">{result.suggestion}</Text>
              {result.exact_matches.length > 0 && (
                <>
                  <Divider label="Found in index" labelPosition="left" />
                  {result.exact_matches.slice(0, 5).map((m) => (
                    <Paper key={m.message_id} p={4} style={{ cursor: 'pointer' }}
                      onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
                      <Text size="xs" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {m.from_addr} · {shortDate(m.received_at)} · {m.folder}
                      </Text>
                    </Paper>
                  ))}
                </>
              )}
              {result.near_matches.length > 0 && (
                <>
                  <Divider label="Similar emails (same keywords)" labelPosition="left" />
                  {result.near_matches.slice(0, 5).map((m) => (
                    <Paper key={m.message_id} p={4} style={{ cursor: 'pointer' }}
                      onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
                      <Text size="xs" lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {m.from_addr} · {shortDate(m.received_at)} · {m.folder}
                      </Text>
                    </Paper>
                  ))}
                </>
              )}
              {result.sender_in_index && result.sender_in_index.count > 0 && result.exact_matches.length === 0 && (
                <Alert color="cyan" variant="light">
                  <Text size="xs">
                    Sender has <b>{result.sender_in_index.count}</b> other emails in: {result.sender_in_index.folders.slice(0, 3).join(', ')}
                  </Text>
                </Alert>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Card>
  );
}

export function IndexPanel({
  state,
  progress,
  prep,
  events,
  folders,
  foldersStatus,
  lastSyncedByFolder,
  mailCountByFolder,
  bulkQueueStatus,
}: {
  state: IndexState | null;
  progress: IndexProgress | null;
  prep: { folder: string; message: string } | null;
  events: BridgeEvent[];
  folders: BridgeBus['outlookFolders'];
  foldersStatus: BridgeBus['outlookFoldersStatus'];
  lastSyncedByFolder: Record<string, number>;
  mailCountByFolder: Record<string, number>;
  bulkQueueStatus: BulkQueueStatus | null;
}): React.JSX.Element {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof api.addinStats>> | null>(null);
  const [interval, setIntervalMin] = useState<number>(state?.auto_index_minutes ?? 0);
  // Round 28-AC: Find-and-remove-duplicates flow with visible progress.
  // Two parallel cleanups can run depending on what the user opted into:
  //   1) BRAIN INDEX dedup — fast SQLite delete (5–10s for 76k rows),
  //      progress polled from /addin/duplicates/job/:id.
  //   2) OUTLOOK MAILBOX dedup — COM addin walks every Inbox + Sent of
  //      every store, fingerprints items locally, deletes extras (moves
  //      to Deleted Items, recoverable). Progress streamed via host
  //      `outlook_dedup_progress` messages. Same pattern as Kutools etc.
  type DupeUI =
    | { phase: 'idle' }
    | { phase: 'scanning' }
    | {
        phase: 'confirming';
        scan: Awaited<ReturnType<typeof api.addinDuplicatesScan>>;
        alsoOutlook: boolean;
        outlookScopeMode: 'all' | 'pick';
        outlookScopeFolders: string[]; // folder paths when mode === 'pick'
      }
    | { phase: 'deleting'; job_id: string; job: Awaited<ReturnType<typeof api.addinDuplicatesJob>> | null; alsoOutlook: boolean; outlookScopeFolders: string[] }
    | { phase: 'outlook'; brainDeleted: number;
        sub_phase: 'preparing' | 'scanning' | 'deleting' | 'done';
        scanned: number; duplicatesFound: number; deletedSoFar: number;
        currentFolder: string | null; folderIdx: number; folderCount: number;
        folderSize: number; folderPos: number;
      }
    | { phase: 'done'; brainDeleted: number; outlookDeleted: number; elapsed_ms: number }
    | { phase: 'error'; error: string };
  const [dupeUI, setDupeUI] = useState<DupeUI>({ phase: 'idle' });
  // Round 28-AC: closing the modal mid-run hides the dialog but the work
  // keeps going (brain job is in the DB; COM addin runs in Outlook). The
  // "minimized" flag lets the floating progress badge re-open the modal.
  const [dupeMinimized, setDupeMinimized] = useState(false);
  const dupeRunning =
    dupeUI.phase === 'scanning' ||
    dupeUI.phase === 'deleting' ||
    dupeUI.phase === 'outlook';
  // Persist the active brain job_id across hard refresh so the dashboard
  // can resume polling and re-show progress without re-running anything.
  // The COM-side outlook scan keeps streaming events on its own; if the
  // bridge listener is alive when they fire, dupeUI will pick them up.
  useEffect(() => {
    if (dupeUI.phase === 'deleting') {
      try { localStorage.setItem('addin_dupe_job_id', dupeUI.job_id); } catch { /* ignore */ }
    } else if (dupeUI.phase === 'idle' || dupeUI.phase === 'done' || dupeUI.phase === 'error') {
      try { localStorage.removeItem('addin_dupe_job_id'); } catch { /* ignore */ }
    }
  }, [dupeUI]);
  // When a run finishes (done/error), auto-pop the modal so the user sees
  // the result even if they had minimized it. Also fire a notification
  // so they get a system-level signal even if they're on a different tab.
  useEffect(() => {
    if (dupeUI.phase === 'done') {
      setDupeMinimized(false);
      notifications.show({
        color: 'green',
        title: 'Duplicate cleanup complete',
        message: `Removed ${dupeUI.brainDeleted.toLocaleString()} from search index${dupeUI.outlookDeleted > 0 ? ` and ${dupeUI.outlookDeleted.toLocaleString()} from Outlook` : ''}.`,
        autoClose: 8000,
      });
    } else if (dupeUI.phase === 'error') {
      setDupeMinimized(false);
      notifications.show({
        color: 'red',
        title: 'Duplicate cleanup failed',
        message: dupeUI.error,
        autoClose: 10000,
      });
    }
  }, [dupeUI]);
  // On mount: if a brain job_id was left over, either (a) surface the
  // completion if the job already finished while we were on another page,
  // or (b) resume the poll loop if it's still in flight. The earlier
  // version silently exited on (a), which meant a user who closed the
  // modal + navigated away missed the completion entirely — they came
  // back to no badge, no notification, no result modal, and reasonably
  // wondered if the dedup ran at all.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only resume
  useEffect(() => {
    let prior: string | null = null;
    try { prior = localStorage.getItem('addin_dupe_job_id'); } catch { /* ignore */ }
    if (!prior) return;
    (async () => {
      try {
        const j = await api.addinDuplicatesJob(prior!);
        if (j.finished) {
          // Job completed (or errored) while we were elsewhere. Set the
          // terminal state so the existing useEffect at line ~12730 fires
          // the completion notification + auto-pops the result modal,
          // and the localStorage-clear effect at line ~12723 cleans up.
          if (j.phase === 'done') {
            setDupeUI({ phase: 'done', brainDeleted: j.deleted, outlookDeleted: 0, elapsed_ms: j.elapsed_ms });
          } else {
            setDupeUI({ phase: 'error', error: j.error ?? 'Job failed' });
          }
          return;
        }
        // Still in flight — set state and let the existing poll loop
        // (kicked off below) catch it. We don't have alsoOutlook info
        // anymore, conservatively assume false.
        setDupeUI({ phase: 'deleting', job_id: prior!, job: j, alsoOutlook: false, outlookScopeFolders: [] });
        let last = j;
        while (true) {
          await new Promise((r) => setTimeout(r, 1000));
          const k = await api.addinDuplicatesJob(prior!);
          last = k;
          setDupeUI((prev) => prev.phase === 'deleting' ? { ...prev, job: k } : prev);
          if (k.finished) break;
        }
        if (last?.phase === 'done') {
          setDupeUI({ phase: 'done', brainDeleted: last.deleted, outlookDeleted: 0, elapsed_ms: last.elapsed_ms });
        } else if (last?.phase === 'error') {
          setDupeUI({ phase: 'error', error: last.error ?? 'Job failed' });
        }
      } catch { /* job may have been swept; ignore */ }
    })();
  }, []);

  // Listen for COM addin's outlook_dedup_progress / outlook_dedup_done
  // events. The dashboard already has a top-level message listener but
  // it's hard-coded to a small set of types; we tap the bridge directly
  // for our own events.
  useEffect(() => {
    function onMsg(e: { data: unknown }) {
      try {
        const obj = typeof e.data === 'string' ? JSON.parse(e.data as string) : e.data;
        if (!obj || typeof obj !== 'object' || !('type' in obj)) return;
        const m = obj as { type: string;
          phase?: string; scanned?: number; duplicates_found?: number;
          deleted?: number; current_folder?: string | null;
          folder_index?: number; folder_count?: number;
          folder_size?: number; folder_pos?: number; errors?: number; error?: string };
        if (m.type === 'outlook_dedup_progress') {
          setDupeUI((prev) => prev.phase !== 'outlook' ? prev : ({
            ...prev,
            sub_phase: (m.phase as 'preparing' | 'scanning' | 'deleting') ?? prev.sub_phase,
            scanned: m.scanned ?? prev.scanned,
            duplicatesFound: m.duplicates_found ?? prev.duplicatesFound,
            deletedSoFar: m.deleted ?? prev.deletedSoFar,
            currentFolder: m.current_folder ?? prev.currentFolder,
            folderIdx: m.folder_index ?? prev.folderIdx,
            folderCount: m.folder_count ?? prev.folderCount,
            folderSize: m.folder_size ?? prev.folderSize,
            folderPos: m.folder_pos ?? prev.folderPos,
          }));
        } else if (m.type === 'outlook_dedup_done') {
          setDupeUI((prev) => prev.phase !== 'outlook' ? prev : ({
            phase: 'done',
            brainDeleted: prev.brainDeleted,
            outlookDeleted: m.deleted ?? 0,
            elapsed_ms: 0,
          }));
        } else if (m.type === 'outlook_dedup_error') {
          setDupeUI({ phase: 'error', error: m.error ?? 'Outlook dedup failed' });
        }
      } catch { /* ignore */ }
    }
    // biome-ignore lint/suspicious/noExplicitAny: WebView2 attaches via window.chrome.webview
    const w = window as any;
    if (w?.chrome?.webview?.addEventListener) {
      w.chrome.webview.addEventListener('message', onMsg);
      return () => { try { w.chrome.webview.removeEventListener('message', onMsg); } catch { /* ignore */ } };
    }
    // Fallback for plain-browser preview (no WebView2): listen to window
    // 'message'. Not used in production but keeps the panel testable.
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // 3.5.55 hotfix: load stats on mount + when index completes ONLY.
  // Pre-fix the dep was `[progress?.processed === undefined]` — a boolean
  // expression that flipped between true/false as `progress` oscillated
  // between null and set. With index_progress events firing 3/sec during
  // active indexing, this effect re-fired ~30+ times per minute, hammering
  // /addin/stats and freezing WebView2. Now: track the COMPLETION
  // transition explicitly via a ref, fire only when running → not-running.
  const wasRunningRef = useRef<boolean>(false);
  useEffect(() => {
    api.addinStats().then(setStats).catch(() => {});
  }, []);
  useEffect(() => {
    const isRunning = !!progress;
    if (wasRunningRef.current && !isRunning) {
      // Just transitioned from running → idle → reload stats once.
      api.addinStats().then(setStats).catch(() => {});
    }
    wasRunningRef.current = isRunning;
  }, [progress]);

  useEffect(() => {
    if (state) setIntervalMin(state.auto_index_minutes);
  }, [state]);

  // Refresh folders list once on mount.
  useEffect(() => {
    if (folders.length === 0) postToHost({ type: 'list_outlook_folders' });
  }, [folders.length]);

  function startIndex(force = false, folderPath?: string) {
    postToHost({ type: 'start_index', folder: folderPath, force_full: force });
  }

  function startIndexAllMailboxes(includeSent = true) {
    postToHost({
      type: 'start_index_all_mailboxes',
      folder_kinds: includeSent ? ['inbox', 'sent'] : ['inbox'],
    });
  }
  async function findAndRemoveDuplicates() {
    setDupeMinimized(false);
    setDupeUI({ phase: 'scanning' });
    try {
      const scan = await api.addinDuplicatesScan();
      if (scan.total_extra_rows === 0) {
        setDupeUI({ phase: 'idle' });
        notifications.show({ message: 'No duplicates found.' });
        return;
      }
      setDupeUI({
        phase: 'confirming', scan, alsoOutlook: false,
        outlookScopeMode: 'all', outlookScopeFolders: [],
      });
    } catch (e) {
      setDupeUI({ phase: 'error', error: e instanceof Error ? e.message : 'Scan failed' });
    }
  }
  // Run the actual delete after the user confirms in the modal.
  async function executeDuplicateDelete(
    alsoOutlook: boolean,
    outlookScopeFolders: string[],
  ) {
    try {
      // Step 1: kick off brain-side delete job (returns immediately).
      const start = await api.addinDuplicatesDelete();
      setDupeUI({ phase: 'deleting', job_id: start.job_id, job: null, alsoOutlook, outlookScopeFolders });
      // Poll job status every 750ms until finished.
      let lastJob: Awaited<ReturnType<typeof api.addinDuplicatesJob>> | null = null;
      while (true) {
        await new Promise((r) => setTimeout(r, 750));
        const j = await api.addinDuplicatesJob(start.job_id);
        lastJob = j;
        setDupeUI((prev) => prev.phase === 'deleting'
          ? { ...prev, job: j }
          : prev);
        if (j.finished) break;
      }
      if (!lastJob || lastJob.phase === 'error') {
        setDupeUI({ phase: 'error', error: lastJob?.error ?? 'Job failed' });
        return;
      }
      const brainDeleted = lastJob.deleted;
      const elapsed_ms = lastJob.elapsed_ms;
      // Step 2: if the user opted in, ask the COM addin to do its OWN
      // dedup pass over Outlook (Kutools-style: walk every Inbox + Sent
      // folder, fingerprint each item by sender+received+subject+size,
      // delete extras). The brain's outlook-targets list isn't useful
      // here because 99%+ of brain rows have no EntryID stored — the
      // COM addin re-scans Outlook directly using its own fingerprints.
      if (!alsoOutlook) {
        setDupeUI({ phase: 'done', brainDeleted, outlookDeleted: 0, elapsed_ms });
        return;
      }
      // Switch state to 'outlook' — the bridge listener will populate
      // counts as outlook_dedup_progress events arrive.
      setDupeUI({
        phase: 'outlook', brainDeleted,
        sub_phase: 'preparing',
        scanned: 0, duplicatesFound: 0, deletedSoFar: 0,
        currentFolder: null, folderIdx: 0, folderCount: 0,
        folderSize: 0, folderPos: 0,
      });
      // Fire-and-forget — the COM addin will push progress + done events.
      // Empty scope_folders means "default scope" (Inbox + Sent of every
      // store) per the COM handler. A non-empty list narrows the walk to
      // exactly those folder paths (+ subfolders).
      postToHost({ type: 'outlook_dedup', scope_folders: outlookScopeFolders });
      // Time-out fallback: if no progress event arrives within 8 seconds,
      // the user probably has an older addin without the outlook_dedup
      // handler. Surface a clear message instead of hanging forever.
      setTimeout(() => {
        setDupeUI((prev) => {
          if (prev.phase !== 'outlook') return prev;
          if (prev.scanned > 0 || prev.deletedSoFar > 0 || prev.folderCount > 0) return prev;
          return {
            phase: 'error',
            error: 'Outlook addin didn\'t respond. Re-install the latest addin (Settings → Outlook addin) and try again.',
          };
        });
      }, 8000);
    } catch (e) {
      setDupeUI({ phase: 'error', error: e instanceof Error ? e.message : 'Delete failed' });
    }
  }
  // Round 47L: ALL means ALL — every folder of every mailbox, force_full=true
  // so every checkpoint is reset and the entire history is re-processed.
  // Confirms first because this can take hours on big mailboxes.
  function forceFullIndexAllMailboxes() {
    if (!confirm(
      'Force re-index EVERYTHING?\n\n' +
      'Walks every folder of every mailbox (incl. subfolders, Junk, Archive) ' +
      'and re-processes every email from scratch. Can take 30+ minutes on a ' +
      'large mailbox. Outlook stays usable but is slower during the run.\n\n' +
      'Use this when you suspect emails are missing from search.'
    )) return;
    postToHost({
      type: 'start_index_all_mailboxes',
      folder_kinds: ['inbox', 'sent'],
      force_full: true,
    });
  }

  function cancelIndex() {
    postToHost({ type: 'cancel_index' });
  }
  function pauseIndex() {
    postToHost({ type: 'pause_index' });
  }
  function resumeIndex() {
    postToHost({ type: 'resume_index' });
  }

  function applyAutoIndex(min: number) {
    setIntervalMin(min);
    postToHost({ type: 'auto_index_set', interval_minutes: min });
  }

  function toggleNewMail(on: boolean) {
    postToHost({ type: on ? 'subscribe_new_mail' : 'unsubscribe_new_mail' });
  }

  function clearLastIndexed(folderPath: string) {
    postToHost({ type: 'clear_last_indexed', folder: folderPath });
  }

  const pct =
    progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;
  const isInWebView2 = !!window.chrome?.webview;

  return (
    <Stack gap="md">
      {!isInWebView2 && (
        <Alert color="yellow" variant="light">
          Indexing is driven by the COM addin. This panel only works inside the addin&rsquo;s
          window — if you&rsquo;re looking at this in a regular browser tab, open the
          ai-outlook ribbon button in Outlook instead.
        </Alert>
      )}

      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Stack gap={0}>
            <Title order={5}>Currently selected Outlook folder</Title>
            {state?.current_folder.name ? (
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  {state.current_folder.name} · {state.current_folder.item_count?.toLocaleString() ?? '?'} items
                </Text>
                <Text size="xs" c="dimmed">
                  Last indexed: {state.current_folder.last_indexed
                    ? formatRelative(state.current_folder.last_indexed)
                    : 'never'}
                  {state.current_folder.path && lastSyncedByFolder[state.current_folder.path]
                    ? ` · synced ${formatRelative(new Date(lastSyncedByFolder[state.current_folder.path]!).toISOString())}`
                    : ''}
                </Text>
                {state.current_folder.last_indexed
                  && Date.now() - new Date(state.current_folder.last_indexed).getTime() > 24 * 60 * 60 * 1000
                  && (
                    <Text size="xs" c="orange">
                      ⚠ New mail arriving here won&rsquo;t appear in the brain until auto-index runs
                      (check the Automation panel — auto-index interval may be 0).
                    </Text>
                  )}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Open a folder in Outlook to enable indexing.
              </Text>
            )}
          </Stack>
          <Button size="xs" variant="subtle" onClick={() => postToHost({ type: 'ready' })}>
            Refresh
          </Button>
        </Group>

        {prep && !progress ? (
          // Round 46: prep card shown while Outlook is opening the table /
          // sorting / counting rows. These calls can each take 2-3s on huge
          // folders and the UI used to look frozen during this window.
          <Stack gap="xs" align="center" py="md">
            <Loader size="sm" />
            <Text size="sm" fw={600}>Preparing index for "{prep.folder}"</Text>
            <Text size="xs" c="dimmed" ta="center">{prep.message}</Text>
            <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 480 }}>
              Outlook is reading the folder. This is the slowest part — once it finishes you'll see live progress and the throughput jumps to thousands of emails per second. Outlook may briefly look unresponsive; that's normal.
            </Text>
          </Stack>
        ) : progress ? (
          // Placeholder vs real progress: the placeholder is what we render
          // when index_state.running=true but no index_progress event has
          // arrived yet. total=0 + elapsed_sec=0 is the unique signature.
          // If we sit in this state for >5s, the host's running flag is
          // almost certainly stuck on a dead task — surface a Reset button
          // that calls force_reset_index_state to clear it.
          (progress.total === 0 && progress.elapsed_sec === 0) ? (
            <Stack gap="xs" align="center" py="md">
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm" fw={500}>Index task running on host…</Text>
              </Group>
              <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 460 }}>
                The Outlook addin reports an indexing task is in progress, but
                no progress updates have arrived yet. This usually means a
                previous task is finishing up, or the task has stalled. Wait
                a few seconds — if no progress appears, click Reset to clear
                the stuck state.
              </Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => postToHost({ type: 'force_reset_index_state' })}
                >
                  Reset stuck index
                </Button>
                <Button size="xs" variant="subtle" onClick={() => postToHost({ type: 'ready' })}>
                  Refresh
                </Button>
              </Group>
            </Stack>
          ) : (
          <Stack gap="xs">
            <Progress value={pct} size="lg" radius="sm" striped animated />
            <Group gap="md">
              <Text size="sm" fw={500}>
                {progress.processed.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)
              </Text>
              <Text size="sm" c="dimmed">
                {progress.rate_per_sec.toFixed(0)} items/s
              </Text>
              <Text size="sm" c="dimmed">
                ETA {formatDuration(progress.eta_sec)}
              </Text>
              <Text size="sm" c="dimmed">
                +{progress.inserted} new · {progress.duplicates_skipped} dup
              </Text>
              {progress.paused && <Badge color="yellow" variant="filled">PAUSED</Badge>}
              {progress.paused ? (
                <Button size="xs" variant="light" color="green" onClick={resumeIndex}>
                  Resume
                </Button>
              ) : (
                <Button size="xs" variant="default" onClick={pauseIndex}>
                  Pause
                </Button>
              )}
              <Button size="xs" color="red" variant="light" onClick={cancelIndex}>
                Cancel
              </Button>
            </Group>
          </Stack>
          )
        ) : (
          <Group gap="xs" wrap="wrap">
            <Button onClick={() => startIndex(false)} disabled={!state?.current_folder.path}>
              Index this folder (incremental)
            </Button>
            <Button
              variant="default"
              onClick={() => startIndex(true)}
              disabled={!state?.current_folder.path}
            >
              Force full re-index
            </Button>
          </Group>
        )}
      </Card>

      {/* 2026-05-23: brain queue depth. Only renders when there's actual
          pending work — keeps the UI clean for the typical idle case while
          giving users visibility when their bulk-index is still draining
          in the background. Numbers come from /addin/index/bulk-status,
          polled every 3s while busy / 30s while idle. */}
      {bulkQueueStatus && (
        bulkQueueStatus.user.pending_emails > 0 ||
        bulkQueueStatus.user.pending_batches > 0 ||
        bulkQueueStatus.user.processing_batches > 0
      ) && (
        <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}>
          <Group justify="space-between" wrap="wrap">
            <Stack gap={2}>
              <Title order={5}>Brain queue: still indexing in background</Title>
              <Text size="sm" c="dimmed">
                {bulkQueueStatus.user.pending_emails.toLocaleString()} email{bulkQueueStatus.user.pending_emails === 1 ? '' : 's'} queued
                · {bulkQueueStatus.user.pending_batches} batch{bulkQueueStatus.user.pending_batches === 1 ? '' : 'es'} pending
                {bulkQueueStatus.user.processing_batches > 0 && ` · ${bulkQueueStatus.user.processing_batches} processing now`}
              </Text>
              <Text size="xs" c="dimmed">
                Search may show partial results until this drains. Brain workers run in parallel.
              </Text>
            </Stack>
            {bulkQueueStatus.paused && <Badge color="yellow" variant="filled">PAUSED</Badge>}
          </Group>
        </Card>
      )}
      <IndexHealthCard />
      <DiagnoseEmailCard />
      <RepairBodiesCard />
      <FolderCoverageCard outlookFolders={folders} mailCountByFolder={mailCountByFolder} />
      {/* (mailCountByFolder threads from AddinPage → IndexPanel → FolderCoverageCard) */}

      {/* Round 28: prominent multi-mailbox button. Walks every Outlook store
          (you have 10 mailboxes — this hits all of them) and indexes Inbox +
          Sent of each. Sequential by folder, but each folder uses the 3×
          parallel pipeline internally. */}
      <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Title order={5}>All my Outlook mailboxes</Title>
            <Text size="sm" c="dimmed">
              Walk every Outlook store (PST/OST) and index Inbox + Sent of each — incremental, so
              re-runs only pick up new mail. For multi-account setups (you have 10 mailboxes).
            </Text>
          </Stack>
          <Group gap="xs">
            <Button
              color="violet"
              onClick={() => startIndexAllMailboxes(true)}
              disabled={!!progress}
            >
              Index ALL mailboxes (Inbox + Sent)
            </Button>
            <Button
              variant="default"
              onClick={() => startIndexAllMailboxes(false)}
              disabled={!!progress}
            >
              Inbox only
            </Button>
            <Button
              color="red"
              variant="light"
              leftSection={<IconReload {...ICON_PROPS} />}
              onClick={forceFullIndexAllMailboxes}
              disabled={!!progress}
            >
              FORCE re-index EVERYTHING (incl. subfolders)
            </Button>
            <Button
              color="orange"
              variant="light"
              leftSection={<IconTrash {...ICON_PROPS} />}
              onClick={findAndRemoveDuplicates}
              disabled={!!progress || dupeUI.phase !== 'idle'}
              loading={dupeUI.phase === 'scanning'}
            >
              Find & remove duplicate emails
            </Button>
          </Group>
        </Group>
      </Card>

      {/* Round 28-AC: floating "in progress" badge — appears whenever a
          dedup is running and the modal is closed. Click to reopen the
          modal at the live state. Survives tab switches inside the addin
          (state lives in IndexPanel) but not full page reloads (brain
          job_id resumes from localStorage; outlook scan keeps streaming
          events as they fire). */}
      {dupeRunning && dupeMinimized && (
        <div
          style={{
            position: 'fixed', bottom: 16, right: 16, zIndex: 1000,
            background: 'var(--mantine-color-orange-6)', color: 'white',
            padding: '10px 14px', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            maxWidth: 360,
          }}
          onClick={() => setDupeMinimized(false)}
          title="Click to view progress"
        >
          <Loader size="xs" color="white" />
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="xs" fw={600} c="white">Removing duplicates…</Text>
            <Text size="xs" c="white" lineClamp={1} style={{ opacity: 0.9 }}>
              {dupeUI.phase === 'scanning' && 'Scanning brain index'}
              {dupeUI.phase === 'deleting' && dupeUI.job &&
                `Brain: ${dupeUI.job.deleted.toLocaleString()} / ${dupeUI.job.total.toLocaleString()}`}
              {dupeUI.phase === 'outlook' && dupeUI.sub_phase === 'scanning' &&
                `Outlook: folder ${dupeUI.folderIdx}/${dupeUI.folderCount} · ${dupeUI.scanned.toLocaleString()} scanned`}
              {dupeUI.phase === 'outlook' && dupeUI.sub_phase === 'deleting' &&
                `Outlook: ${dupeUI.deletedSoFar.toLocaleString()} / ${dupeUI.duplicatesFound.toLocaleString()} deleted`}
              {dupeUI.phase === 'outlook' && dupeUI.sub_phase === 'preparing' && 'Preparing Outlook scan'}
            </Text>
          </Stack>
          <Text size="xs" c="white" style={{ opacity: 0.85 }}>↗</Text>
        </div>
      )}

      {/* Round 28-AC: duplicates flow modal — replaces the old confirm()
          pop-up with phased progress so the user can see what's happening
          on a 76k-row delete (used to hang silently for several minutes).
          Closable at any phase: closing while running just hides the
          dialog (a floating badge surfaces above), the actual work
          (brain job + COM scan) keeps running on its own. */}
      <Modal
        opened={dupeUI.phase !== 'idle' && !dupeMinimized}
        onClose={() => {
          // Closing during running phases minimizes — work continues.
          // Closing at done/error/confirming dismisses the flow entirely.
          if (dupeRunning) {
            setDupeMinimized(true);
            return;
          }
          setDupeUI({ phase: 'idle' });
          setDupeMinimized(false);
        }}
        title="Find & remove duplicate emails"
        size="md"
        closeOnClickOutside
        closeOnEscape
        withCloseButton
      >
        {dupeUI.phase === 'scanning' && (
          <Stack align="center" py="lg" gap="xs">
            <Loader />
            <Text size="sm">Scanning mailbox for duplicates…</Text>
            <Text size="xs" c="dimmed">Walking 400k+ rows. Usually 2–5 seconds.</Text>
          </Stack>
        )}
        {dupeUI.phase === 'confirming' && (
          <Stack gap="sm">
            <Text size="sm">
              Found <b>{dupeUI.scan.total_extra_rows.toLocaleString()}</b> byte-identical duplicate{dupeUI.scan.total_extra_rows === 1 ? '' : 's'} (same body, subject, sender) across <b>{dupeUI.scan.groups.length}</b> top groups in your search index.
            </Text>
            <Text size="xs" c="dimmed">
              This count is across your <b>entire indexed mail</b>, not just any folder you may pick below — the brain's search index is one corpus per user. Folder picking only affects the optional Outlook-side delete pass.
            </Text>
            {(dupeUI.scan.near_extra_rows ?? 0) > dupeUI.scan.total_extra_rows && (
              <Text size="xs" c="dimmed">
                There are also <b>{(dupeUI.scan.near_extra_rows ?? 0).toLocaleString()}</b> near-duplicates (same subject + sender + day, slightly different body). Those are NOT removed — only exact byte-for-byte copies.
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Per group, the row with the most enrichment is kept; the rest are removed. Search and AI features are unaffected — the keeper still answers every search the duplicates would have.
            </Text>
            <Card padding="sm" withBorder radius="sm" style={{ background: 'var(--mantine-color-gray-0)' }}>
              <Stack gap="xs">
                <Checkbox
                  label="Also delete the duplicates from Outlook itself (moves to Deleted Items)"
                  description="Recoverable — the COM addin moves each duplicate to your Deleted Items folder. The keeper stays in its original folder."
                  checked={dupeUI.alsoOutlook}
                  onChange={(e) => setDupeUI({ ...dupeUI, alsoOutlook: e.currentTarget.checked })}
                />
                {dupeUI.alsoOutlook && (() => {
                  // Round 28-AC: folder scope picker. Filter outlookFolders
                  // to mail-only paths (drop Calendar/Contacts/PersonMetadata
                  // /etc — same rule as the brain's isMailFolderPath helper).
                  // Group remaining folders by store name for readability.
                  const NON_MAIL = new Set([
                    'Calendar', 'Contacts', 'Tasks', 'Journal', 'Notes', 'Files',
                    'Yammer Root', 'Inbound', 'Outbound', 'Feeds',
                    'PersonMetadata', 'MeContact',
                    'Recipient Cache', 'PeopleCentricConversation Buddies', 'GAL Contacts',
                    'Companies', 'Organizational Contacts', 'ExternalContacts',
                    'Conversation Action Settings', 'Quick Step Settings',
                    'Conversation History', 'Team Chat',
                    'Mail Delivery System', 'RSS Feeds',
                  ]);
                  function isMailPath(p: string): boolean {
                    const segments = p.split('\\').filter(Boolean);
                    if (segments.length < 2) return false;
                    for (let i = 1; i < segments.length; i++) {
                      const seg = segments[i]!;
                      if (NON_MAIL.has(seg)) return false;
                      if (/^\{[0-9A-Fa-f-]+\}$/.test(seg)) return false;
                      if (seg.startsWith('Sync Issues')) return false;
                    }
                    return true;
                  }
                  const mailFolders = folders
                    .filter((f) => isMailPath(f.path))
                    .filter((f) => (f.item_count ?? 0) > 0);
                  const grouped = new Map<string, typeof mailFolders>();
                  for (const f of mailFolders) {
                    const m = f.path.match(/^\\\\([^\\]+)\\/);
                    const store = m ? m[1]! : '(unknown)';
                    const arr = grouped.get(store) ?? [];
                    arr.push(f);
                    grouped.set(store, arr);
                  }
                  const sortedStores = [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
                  const totalSelected = dupeUI.outlookScopeFolders.length;
                  return (
                    <Stack gap="xs" pl="md">
                      <SegmentedControl
                        size="xs"
                        value={dupeUI.outlookScopeMode}
                        onChange={(v) => setDupeUI({
                          ...dupeUI,
                          outlookScopeMode: v as 'all' | 'pick',
                          // Wipe selection when switching back to "all".
                          outlookScopeFolders: v === 'all' ? [] : dupeUI.outlookScopeFolders,
                        })}
                        data={[
                          { label: 'All Inboxes + Sent (recommended)', value: 'all' },
                          { label: 'Pick specific folders', value: 'pick' },
                        ]}
                      />
                      {dupeUI.outlookScopeMode === 'pick' && (
                        <>
                          <Group gap="xs">
                            <Text size="xs" c="dimmed">{totalSelected} selected</Text>
                            <Button size="compact-xs" variant="subtle"
                              onClick={() => setDupeUI({ ...dupeUI, outlookScopeFolders: mailFolders.map((f) => f.path) })}>
                              All
                            </Button>
                            <Button size="compact-xs" variant="subtle"
                              onClick={() => setDupeUI({ ...dupeUI, outlookScopeFolders: [] })}>
                              None
                            </Button>
                            <Button size="compact-xs" variant="subtle"
                              onClick={() => setDupeUI({
                                ...dupeUI,
                                outlookScopeFolders: mailFolders
                                  .filter((f) => /\\(Inbox|Sent Items|Sent)$/i.test(f.path))
                                  .map((f) => f.path),
                              })}>
                              Inbox + Sent only
                            </Button>
                          </Group>
                          <ScrollArea.Autosize mah={220} type="auto">
                            <Stack gap="xs">
                              {sortedStores.length === 0 && (
                                <Text size="xs" c="dimmed">No folders to show — open the Index page once to make Outlook list them.</Text>
                              )}
                              {sortedStores.map(([store, list]) => {
                                const allInStore = list.every((f) => dupeUI.outlookScopeFolders.includes(f.path));
                                return (
                                  <Stack gap={2} key={store}>
                                    <Group justify="space-between" gap="xs">
                                      <Text size="xs" fw={600} lineClamp={1}>{store}</Text>
                                      <Button size="compact-xs" variant="subtle"
                                        onClick={() => setDupeUI({
                                          ...dupeUI,
                                          outlookScopeFolders: allInStore
                                            ? dupeUI.outlookScopeFolders.filter((p) => !list.some((f) => f.path === p))
                                            : Array.from(new Set([...dupeUI.outlookScopeFolders, ...list.map((f) => f.path)])),
                                        })}>
                                        {allInStore ? 'Deselect store' : 'Select store'}
                                      </Button>
                                    </Group>
                                    {list
                                      .sort((a, b) => (b.item_count ?? 0) - (a.item_count ?? 0))
                                      .slice(0, 12)
                                      .map((f) => (
                                        <Checkbox
                                          key={f.path}
                                          size="xs"
                                          checked={dupeUI.outlookScopeFolders.includes(f.path)}
                                          onChange={(e) => setDupeUI({
                                            ...dupeUI,
                                            outlookScopeFolders: e.currentTarget.checked
                                              ? [...dupeUI.outlookScopeFolders, f.path]
                                              : dupeUI.outlookScopeFolders.filter((p) => p !== f.path),
                                          })}
                                          label={
                                            <Text size="xs" lineClamp={1}>
                                              {f.path.replace(/^\\\\[^\\]+\\/, '')}{' '}
                                              <Text span size="xs" c="dimmed">({f.item_count?.toLocaleString() ?? '?'})</Text>
                                            </Text>
                                          }
                                        />
                                      ))}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </ScrollArea.Autosize>
                          <Text size="xs" c="dimmed">
                            Subfolders of any selected folder are also walked. Picking only what you need keeps the scan fast.
                          </Text>
                        </>
                      )}
                    </Stack>
                  );
                })()}
              </Stack>
            </Card>
            {dupeUI.scan.groups.slice(0, 5).length > 0 && (
              <Stack gap={2} mt="xs">
                <Text size="xs" fw={600}>Top duplicate groups:</Text>
                {dupeUI.scan.groups.slice(0, 5).map((g, i) => (
                  <Text key={`${g.subj_key}-${i}`} size="xs" c="dimmed" lineClamp={1}>
                    ×{g.dup_count} · {g.from_key || '(unknown)'} · {(g.subj_key || '(no subject)').slice(0, 60)}
                  </Text>
                ))}
              </Stack>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setDupeUI({ phase: 'idle' })}>Cancel</Button>
              <Button
                color="orange"
                disabled={dupeUI.alsoOutlook && dupeUI.outlookScopeMode === 'pick' && dupeUI.outlookScopeFolders.length === 0}
                onClick={() => executeDuplicateDelete(
                  dupeUI.alsoOutlook,
                  dupeUI.alsoOutlook && dupeUI.outlookScopeMode === 'pick'
                    ? dupeUI.outlookScopeFolders
                    : [],
                )}
              >
                Remove {dupeUI.scan.total_extra_rows.toLocaleString()} duplicate{dupeUI.scan.total_extra_rows === 1 ? '' : 's'}
              </Button>
            </Group>
          </Stack>
        )}
        {dupeUI.phase === 'deleting' && (() => {
          const j = dupeUI.job;
          const total = j?.total ?? 0;
          const deleted = j?.deleted ?? 0;
          const pct = total > 0 ? Math.round((deleted / total) * 100) : 0;
          return (
            <Stack gap="sm">
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="sm" fw={500}>
                  {j?.phase === 'scanning' ? 'Identifying duplicates…' : 'Removing duplicates from search index…'}
                </Text>
              </Group>
              {total > 0 && (
                <>
                  <Progress value={pct} size="md" striped animated />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">{deleted.toLocaleString()} / {total.toLocaleString()} ({pct}%)</Text>
                    <Text size="xs" c="dimmed">{((j?.elapsed_ms ?? 0) / 1000).toFixed(1)}s</Text>
                  </Group>
                </>
              )}
              <Text size="xs" c="dimmed">
                Running on the server. You can close this dialog — the work continues in the background and a small badge will appear at the bottom-right showing live progress. Click it to reopen this view. You'll get a notification when it's done.
              </Text>
            </Stack>
          );
        })()}
        {dupeUI.phase === 'outlook' && (() => {
          const folderPct = dupeUI.folderSize > 0
            ? Math.round((dupeUI.folderPos / dupeUI.folderSize) * 100)
            : 0;
          const overallPct = dupeUI.folderCount > 0
            ? Math.round(((dupeUI.folderIdx - 1) + (dupeUI.folderSize > 0 ? dupeUI.folderPos / dupeUI.folderSize : 0)) / dupeUI.folderCount * 100)
            : 0;
          const deletePct = dupeUI.duplicatesFound > 0
            ? Math.round((dupeUI.deletedSoFar / dupeUI.duplicatesFound) * 100)
            : 0;
          return (
            <Stack gap="sm">
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="sm" fw={500}>
                  {dupeUI.sub_phase === 'preparing' && 'Preparing Outlook scan…'}
                  {dupeUI.sub_phase === 'scanning' && 'Scanning Outlook for duplicates…'}
                  {dupeUI.sub_phase === 'deleting' && 'Deleting duplicates from Outlook…'}
                </Text>
              </Group>
              {dupeUI.sub_phase === 'scanning' && (
                <>
                  <Progress value={overallPct} size="md" striped animated color="orange" />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      Folder {dupeUI.folderIdx} of {dupeUI.folderCount} · {dupeUI.scanned.toLocaleString()} items scanned · {dupeUI.duplicatesFound.toLocaleString()} duplicates found
                    </Text>
                    <Text size="xs" c="dimmed">{overallPct}%</Text>
                  </Group>
                  {dupeUI.currentFolder && (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {dupeUI.currentFolder} · {dupeUI.folderPos.toLocaleString()} / {dupeUI.folderSize.toLocaleString()} ({folderPct}%)
                    </Text>
                  )}
                </>
              )}
              {dupeUI.sub_phase === 'deleting' && (
                <>
                  <Progress value={deletePct} size="md" striped animated color="red" />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">{dupeUI.deletedSoFar.toLocaleString()} / {dupeUI.duplicatesFound.toLocaleString()} deleted</Text>
                    <Text size="xs" c="dimmed">{deletePct}%</Text>
                  </Group>
                </>
              )}
              <Text size="xs" c="dimmed">
                The addin walks every Inbox + Sent of every store, fingerprints each item locally, and deletes extras. Recoverable — items go to Deleted Items, not permanently deleted. Outlook stays usable but a little slower during the scan.
              </Text>
              <Text size="xs" c="dimmed">
                Safe to close — the scan continues in Outlook even after closing the dialog. Live progress badge stays visible at the bottom-right; click it to reopen this view, or wait for the completion notification.
              </Text>
            </Stack>
          );
        })()}
        {dupeUI.phase === 'done' && (
          <Stack gap="sm">
            <Alert color="green" variant="light" title="Done">
              <Stack gap={2}>
                <Text size="sm"><b>{dupeUI.brainDeleted.toLocaleString()}</b> duplicate row{dupeUI.brainDeleted === 1 ? '' : 's'} removed from search index.</Text>
                {dupeUI.outlookDeleted > 0 && (
                  <Text size="sm"><b>{dupeUI.outlookDeleted.toLocaleString()}</b> queued for deletion in Outlook (moves to Deleted Items in the background).</Text>
                )}
                <Text size="xs" c="dimmed">Took {(dupeUI.elapsed_ms / 1000).toFixed(1)}s.</Text>
              </Stack>
            </Alert>
            <Group justify="flex-end">
              <Button onClick={() => setDupeUI({ phase: 'idle' })}>Close</Button>
            </Group>
          </Stack>
        )}
        {dupeUI.phase === 'error' && (
          <Stack gap="sm">
            <Alert color="red" variant="light" title="Failed">
              <Text size="sm">{dupeUI.error}</Text>
            </Alert>
            <Group justify="flex-end">
              <Button onClick={() => setDupeUI({ phase: 'idle' })}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Card padding="md" radius="md" withBorder>
        <Stack gap="sm">
          <Title order={5}>Automation</Title>
          <Group gap="lg" wrap="wrap" align="center">
            <Stack gap={0} style={{ minWidth: 240 }}>
              <Text size="sm" fw={500}>
                Auto-index (incremental)
              </Text>
              <Text size="xs" c="dimmed">
                Re-checks the active folder every N minutes. 0 = off.
              </Text>
              <Group gap="xs" mt={4}>
                <NumberInput
                  size="xs"
                  value={interval}
                  onChange={(v) => setIntervalMin(typeof v === 'number' ? v : 0)}
                  min={0}
                  max={1440}
                  w={100}
                />
                <Button size="xs" onClick={() => applyAutoIndex(interval)}>
                  Apply
                </Button>
                <Button size="xs" variant="subtle" onClick={() => applyAutoIndex(0)}>
                  Off
                </Button>
              </Group>
            </Stack>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                Live indexing (Outlook NewMailEx)
              </Text>
              <Text size="xs" c="dimmed">
                Index any new mail the moment Outlook receives it.
              </Text>
              <Switch
                mt={4}
                checked={!!state?.new_mail_hooked}
                onChange={(e) => toggleNewMail(e.currentTarget.checked)}
                label={state?.new_mail_hooked ? 'Live (on)' : 'Off'}
              />
            </Stack>
          </Group>
        </Stack>
      </Card>

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Title order={5}>Folders indexed</Title>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => postToHost({ type: 'list_outlook_folders' })}
              >
                Reload list
              </Button>
            </Group>
            <ScrollArea h={300}>
              {folders.length === 0 ? (
                foldersStatus.error ? (
                  <Alert color="red" variant="light">
                    Could not enumerate folders: {foldersStatus.error}
                  </Alert>
                ) : foldersStatus.received ? (
                  <Alert color="yellow" variant="light">
                    No folders found in this Outlook profile. Open Outlook, expand a mailbox in
                    the left pane, then click Reload list.
                  </Alert>
                ) : (
                  <FolderLoadingState />
                )
              ) : (
                <Stack gap={2}>
                  {folders.map((f) => (
                    <Paper
                      key={f.path}
                      p="xs"
                      style={{
                        borderLeft: f.last_indexed
                          ? '3px solid var(--mantine-color-green-5)'
                          : '3px solid var(--mantine-color-gray-4)',
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap" gap="xs">
                        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} lineClamp={1}>
                            {f.name}
                          </Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {f.path} · {f.item_count?.toLocaleString() ?? '?'} items
                          </Text>
                        </Stack>
                        <Stack gap={0} align="flex-end">
                          <Text size="xs" c="dimmed">
                            {f.last_indexed ? formatRelative(f.last_indexed) : 'never'}
                          </Text>
                          <Group gap={4}>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              onClick={() => startIndex(false, f.path)}
                            >
                              Index
                            </Button>
                            {f.last_indexed && (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                color="red"
                                onClick={() => clearLastIndexed(f.path)}
                              >
                                Reset
                              </Button>
                            )}
                          </Group>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </ScrollArea>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Title order={5}>Activity log</Title>
              <Text size="xs" c="dimmed">
                {events.length} events
              </Text>
            </Group>
            <ScrollArea h={300}>
              {events.length === 0 ? (
                <Text c="dimmed" size="sm">
                  No activity yet.
                </Text>
              ) : (
                <Stack gap={4}>
                  {events.map((e, i) => (
                    <Group
                      key={`${e.ts}-${i}`}
                      gap="xs"
                      wrap="nowrap"
                      align="flex-start"
                    >
                      <ThemeIcon
                        size="xs"
                        variant="light"
                        color={
                          e.level === 'error'
                            ? 'red'
                            : e.level === 'warn'
                              ? 'yellow'
                              : e.level === 'success'
                                ? 'green'
                                : 'blue'
                        }
                        radius="xl"
                      >
                        {e.level === 'error' ? '!' : e.level === 'warn' ? '?' : e.level === 'success' ? '✓' : 'i'}
                      </ThemeIcon>
                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" lineClamp={2}>
                          {e.message}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(e.ts).toLocaleTimeString()}
                        </Text>
                      </Stack>
                    </Group>
                  ))}
                </Stack>
              )}
            </ScrollArea>
          </Card>

          {stats && (
            <Card padding="md" radius="md" withBorder mt="md">
              <Title order={5} mb="xs">
                Brain has indexed
              </Title>
              <Group justify="space-between">
                <Text size="lg" fw={700}>
                  {stats.total_indexed.toLocaleString()}
                </Text>
                <Text size="sm" c="dimmed">
                  total emails
                </Text>
              </Group>
              <Group justify="space-between" mt="xs">
                <Text size="sm" c="dimmed">
                  +{stats.today} today
                </Text>
                <Text size="sm" c="dimmed">
                  +{stats.last_7d} this week
                </Text>
              </Group>
            </Card>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

// =========================================================================
// Settings.
// =========================================================================

// =========================================================================
// Round 35: Cases (projects) + Audit log.
// =========================================================================

